import { Box, Button, Form } from '@factorialco/deprecated-design-system'
import {
  InfojobsOfferDocument,
  InfojobsOfferValuesFragment,
  OfferFieldsOptionsDocument,
  UpdateOfferDocument,
  UpdateOfferMutationVariables,
} from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { ErrorInformation } from './ErrorInformation'
import { FillInformationSkeleton } from './FillInformationSkeleton'
import { SuboptionSelector } from './SuboptionSelector'
import { DEFAULT_JOB_SUBINDUSTRY, JobAttributes, checkErrors, mapOptions } from './utils'

interface Props {
  companyId: string
  jobAttributes: JobAttributes
  offer: InfojobsOfferValuesFragment
  onSuccess: () => void
  onClose: () => void
}

export const EditOffer = ({ companyId, jobAttributes, offer, onSuccess, onClose }: Props) => {
  const { loading, data } = useQuery(OfferFieldsOptionsDocument, {
    variables: {
      companyId: companyId,
    },
  })
  const [updateOffer, { loading: updatingOffer }] = useMutation(UpdateOfferDocument, {
    extractorFn: (updatedOffer) =>
      updatedOffer.integrationsInfojobsMutations.updateIntegrationsinfojobsOffer,
  })

  const form = useForm<UpdateOfferMutationVariables>({
    defaultValues: {
      id: offer.id,
      contractTypeId: offer.contractTypeId,
      minimumExperienceId: offer.minimumExperienceId,
      minimumStudiesId: offer.minimumStudiesId,
      salaryPerId: offer.salaryPerId,
      salaryFromId: offer.salaryFromId,
      salaryToId: offer.salaryToId,
      workingDayId: offer.workingDayId,
      jobSubindustryId: offer.jobSubindustryId ?? DEFAULT_JOB_SUBINDUSTRY,
      levelId: offer.levelId,
    },
    onSubmit: async (values, { setErrors }) => {
      const response = await updateOffer({
        variables: values,
        refetchQueries: [InfojobsOfferDocument],
      })
      const handled = handleResult(response, (formErrors) => setErrors(formErrors))
      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onSuccess()
      }

      return handled
    },
  })

  const infojobsData = data?.integrationsInfojobs

  const errors: string[] = checkErrors(jobAttributes)

  if (errors.length > 0) {
    return <ErrorInformation errors={errors} onClose={onClose} />
  }

  return (
    <Modal
      onClose={onClose}
      title={i18n.t('ats.job_details.promotion.infojobs.job.manage.edit.title')}
      illustration={'document-edit'}
      description={i18n.t('ats.job_details.promotion.infojobs.job.manage.edit.description')}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Box flexDirection="row" gap="s16">
          <Button type="secondary" onClick={() => onClose()}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={loading} type="negative" submit loading={updatingOffer}>
            {i18n.t('common.publish')}
          </Button>
        </Box>
      }
    >
      <Box gap="s16">
        <Callout
          type="info"
          title={i18n.t('ats.job_details.promotion.infojobs.job.manage.edit.alert.title')}
          description={i18n.t(
            'ats.job_details.promotion.infojobs.job.manage.edit.alert.description'
          )}
        />

        {loading ? (
          <FillInformationSkeleton />
        ) : (
          <Form padding={null}>
            <Select
              label={i18n.t(
                'ats.job_details.promotion.infojobs.job.information.form_fields.contract_type'
              )}
              options={mapOptions(infojobsData?.contractTypes.nodes)}
              none={i18n.t('common.select')}
              mandatory
              onChange={(value) => form.setFieldValue('contractTypeId', value)}
              value={form.values.contractTypeId ?? undefined}
            />
            <Box gap="s16" flexDirection="row">
              <Select
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.minimum_studies'
                )}
                options={mapOptions(infojobsData?.studies.nodes)}
                none={i18n.t('common.select')}
                mandatory
                onChange={(value) => form.setFieldValue('minimumStudiesId', value)}
                value={form.values.minimumStudiesId ?? undefined}
              />
              <Select
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.minimum_experience'
                )}
                options={mapOptions(infojobsData?.experience.nodes)}
                none={i18n.t('common.select')}
                mandatory
                onChange={(value) => form.setFieldValue('minimumExperienceId', value)}
                value={form.values.minimumExperienceId ?? undefined}
              />
            </Box>
            <Select
              label={i18n.t(
                'ats.job_details.promotion.infojobs.job.information.form_fields.salary_period'
              )}
              options={mapOptions(infojobsData?.salaryPeriods.nodes)}
              none={i18n.t('common.select')}
              mandatory
              onChange={(value) => form.setFieldValue('salaryPerId', value)}
              value={form.values.salaryPerId ?? undefined}
            />
            <Box gap="s16" flexDirection="row">
              <Select
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.salary_ranges_from'
                )}
                options={mapOptions(infojobsData?.salaryRanges.nodes)}
                none={i18n.t('common.select')}
                mandatory
                onChange={(value) => form.setFieldValue('salaryFromId', value)}
                value={form.values.salaryFromId ?? undefined}
              />
              <Select
                label={i18n.t(
                  'ats.job_details.promotion.infojobs.job.information.form_fields.salary_ranges_to'
                )}
                options={mapOptions(infojobsData?.salaryRanges.nodes)}
                none={i18n.t('common.select')}
                mandatory
                onChange={(value) => form.setFieldValue('salaryToId', value)}
                value={form.values.salaryToId ?? undefined}
              />
            </Box>
            <Select
              label={i18n.t('ats.job_details.promotion.infojobs.job.information.form_fields.level')}
              options={mapOptions(infojobsData?.level.nodes)}
              none={i18n.t('common.select')}
              mandatory
              onChange={(value) => form.setFieldValue('levelId', value)}
              value={form.values.levelId ?? undefined}
            />
            <SuboptionSelector
              label={i18n.t(
                'ats.job_details.promotion.infojobs.job.information.form_fields.job_subindustry'
              )}
              companyId={companyId}
              category={'job_subindustry'}
              parentId={offer.jobIndustryId}
              onChange={(value: string) => form.setFieldValue('jobSubindustryId', value)}
              suboptionId={form.values.jobSubindustryId}
            />
          </Form>
        )}
      </Box>
    </Modal>
  )
}
